import type {
	AnalyticsDashboardData,
	API_GetAnalyticsDashboardResponse,
	API_GetUserDashboardResponse,
	UserDashboardData,
} from "@pilatestime/shared-types";
import { queryOptions } from "@tanstack/react-query";
import api from "./api_client";

// ============================================================================
// USER DASHBOARD API (Personal Dashboard)
// ============================================================================

// API function for user dashboard
export const fetchUserDashboard = (
	token: string,
	organizationId: string,
): Promise<UserDashboardData> =>
	api
		.get(`/organizations/${organizationId}/analytics/user-dashboard`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => {
			const response = res.data as API_GetUserDashboardResponse;
			return response.data;
		});

// Query options for user dashboard
export const fetchUserDashboardQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
) =>
	queryOptions({
		queryKey: ["user_dashboard", organizationId, userId],
		queryFn: () => fetchUserDashboard(token, organizationId),
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
		retry: 2,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
	});

// ============================================================================
// BUSINESS ANALYTICS API (Business Analytics Page)
// ============================================================================

// API function for business analytics dashboard
export const fetchAnalyticsDashboard = (
	token: string,
	organizationId: string,
): Promise<AnalyticsDashboardData> =>
	api
		.get(`/organizations/${organizationId}/analytics/dashboard`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => {
			const response = res.data as API_GetAnalyticsDashboardResponse;
			return response.data;
		});

// Query options for business analytics dashboard
export const fetchAnalyticsDashboardQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
) =>
	queryOptions({
		queryKey: ["analytics_dashboard", organizationId, userId],
		queryFn: () => fetchAnalyticsDashboard(token, organizationId),
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
		retry: 2, // Retry failed requests twice
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
	});
